/**
 * Rich text editor for chapter content (TipTap-based).
 *
 * Toolbar: bold, italic, underline, paragraph styles, alignment, lists, image upload.
 * Image upload uploads to the backend (GridFS) and inserts a URL pointing to
 * /api/images/{id}. The returned URL is absolute (REACT_APP_BACKEND_URL prefixed).
 */
import { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import {
  TextB,
  TextItalic,
  TextStrikethrough,
  TextH,
  TextHOne,
  TextHTwo,
  TextAlignLeft,
  TextAlignCenter,
  TextAlignRight,
  ListBullets,
  ListNumbers,
  ImageSquare,
  ArrowUUpLeft,
  ArrowUUpRight,
} from "@phosphor-icons/react";
import { coursesAdminApi, imageUrl } from "../../api/courses";

const Btn = ({ active, disabled, onClick, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-700 transition-colors ${
      active
        ? "bg-slate-900 text-white"
        : "hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
    }`}
  >
    {children}
  </button>
);

const Sep = () => <span className="mx-1 h-6 w-px bg-slate-200" />;

export default function ChapterRichEditor({ value, onChange, placeholder }) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Image.configure({ inline: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({
        placeholder: placeholder || "Commencez à écrire…",
      }),
    ],
    content: value || { type: "doc", content: [] },
    editorProps: {
      attributes: {
        class:
          "tiptap-editor prose prose-slate max-w-none focus:outline-none min-h-[500px] px-6 py-5",
      },
    },
    onUpdate: ({ editor: ed }) => {
      // Defer to next tick to avoid React "setState during render" warnings
      // when the editor emits during its initial mount.
      const json = ed.getJSON();
      queueMicrotask(() => onChange?.(json));
    },
  });

  // Sync external value changes (e.g. when switching chapters)
  useEffect(() => {
    if (!editor || !value) return;
    const current = JSON.stringify(editor.getJSON());
    const incoming = JSON.stringify(value);
    if (current !== incoming) {
      editor.commands.setContent(value, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const handleImagePick = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !editor) return;
      try {
        const res = await coursesAdminApi.uploadImage(file);
        editor.chain().focus().setImage({ src: imageUrl(res.url) }).run();
      } catch (err) {
        console.error("Image upload failed", err);
      }
    },
    [editor],
  );

  if (!editor) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {/* Toolbar */}
      <div
        data-testid="chapter-editor-toolbar"
        className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-white/95 backdrop-blur px-3 py-2"
      >
        <Btn
          label="Annuler"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <ArrowUUpLeft size={18} weight="regular" />
        </Btn>
        <Btn
          label="Rétablir"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <ArrowUUpRight size={18} weight="regular" />
        </Btn>

        <Sep />

        <Btn
          label="Paragraphe"
          active={editor.isActive("paragraph")}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          <TextH size={18} weight="regular" />
        </Btn>
        <Btn
          label="Titre 1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <TextHOne size={18} weight="regular" />
        </Btn>
        <Btn
          label="Titre 2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <TextHTwo size={18} weight="regular" />
        </Btn>

        <Sep />

        <Btn
          label="Gras"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <TextB size={18} weight="bold" />
        </Btn>
        <Btn
          label="Italique"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <TextItalic size={18} weight="regular" />
        </Btn>
        <Btn
          label="Barré"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <TextStrikethrough size={18} weight="regular" />
        </Btn>

        <Sep />

        <Btn
          label="Aligner à gauche"
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
        >
          <TextAlignLeft size={18} weight="regular" />
        </Btn>
        <Btn
          label="Centrer"
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
        >
          <TextAlignCenter size={18} weight="regular" />
        </Btn>
        <Btn
          label="Aligner à droite"
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
        >
          <TextAlignRight size={18} weight="regular" />
        </Btn>

        <Sep />

        <Btn
          label="Liste à puces"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListBullets size={18} weight="regular" />
        </Btn>
        <Btn
          label="Liste numérotée"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListNumbers size={18} weight="regular" />
        </Btn>

        <Sep />

        <Btn
          label="Image"
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageSquare size={18} weight="regular" />
        </Btn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImagePick}
          data-testid="chapter-editor-image-input"
        />
      </div>

      <EditorContent editor={editor} data-testid="chapter-editor-content" />
    </div>
  );
}
